import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Paging from '../../component/Paging';
import request from '../../../utils/Request.utils';
import { ToastContainer, toast } from 'react-toastify';
import CheckedSVG from '../../../assets/img/check-solid.svg';
import Modal from '../../component/Modal';

const LIST_PAGE_SIZE = 10;
const formatNumber = (value) => Number(value || 0).toLocaleString();

const Notice = ({ isLoggedIn }) => {
  const navigate = useNavigate();
  const [notices, setNotices] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currPage, setCurrPage] = useState(1);
  const [totalCnt, setTotalCnt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedNotices, setSelectedNotices] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const sortNotices = (notices) => {
    return [...notices].sort((a, b) => {
      if (a.board_is_main === "true" && b.board_is_main !== "true") return -1;
      if (a.board_is_main !== "true" && b.board_is_main === "true") return 1;
      return new Date(b.reg_dtm) - new Date(a.reg_dtm);
    });
  };

  const successHandler = useCallback((response) => {
    if (response.messageCode === 200) {
      const sortedNotices = sortNotices(response.data.dataList || []);
      setNotices(sortedNotices);
      setTotalPages(Number(response.data.tot_page) || 0);
      setTotalCnt(Number(response.data.totalCnt) || 0);
      setSelectedNotices([]);
      setLoading(false);
    } else {
      toast.error("공지사항 정보를 불러올 수 없습니다.");
      setLoading(false);
    }
  }, []);

  const errorHandler = useCallback((error) => {
    toast.error("데이터를 불러오는 중 오류가 발생했습니다.");
    setLoading(false);
  }, []);

  const fetchNotices = useCallback(() => {
    setLoading(true);
    const params = {
      curr_page: currPage,
      per_page: LIST_PAGE_SIZE,
      sort_col: 'reg_dtm',
      sort_by: 'desc',
      board_stat: 'all',
    };
    request.get("/board/list/admin/BD_NOTICE", params, successHandler, errorHandler);
  }, [currPage, successHandler, errorHandler]);

  useEffect(() => {
    fetchNotices();
  }, [currPage, fetchNotices]);

  const handlePageChange = (page) => {
    setCurrPage(page);
  };

  const handleSelectNotice = (noticeId) => {
    setSelectedNotices(prevSelectedNotices =>
      prevSelectedNotices.includes(noticeId)
        ? prevSelectedNotices.filter(id => id !== noticeId)
        : [...prevSelectedNotices, noticeId]
    );
  };
  const handleSelectAll = () => {
    if (notices.length === 0) {
      setSelectedNotices([]);
      return;
    }

    if (selectedNotices.length === notices.length) {
      setSelectedNotices([]);
    } else {
      setSelectedNotices(notices.map(notice => notice.board_seq));
    }
  };
  const openModal = () => {
    if (selectedNotices.length === 0) {
      toast.error("삭제할 공지사항을 선택해 주세요.");
      return;
    }
    setModalIsOpen(true);
  };
  const closeModal = () => setModalIsOpen(false);

  const handleDelete = async () => {
    if (selectedNotices.length === 0) {
      toast.error("삭제할 공지사항을 선택해 주세요.");
      closeModal();
      return;
    }

    try {
      await request.del("/board/BD_NOTICE", { board_seqs: selectedNotices }, () => {
        toast.success("공지사항이 성공적으로 삭제되었습니다.");
        const nextTotalCnt = Math.max(totalCnt - selectedNotices.length, 0);
        const nextTotalPages = Math.ceil(nextTotalCnt / LIST_PAGE_SIZE);
        const nextPage = Math.min(currPage, nextTotalPages || 1);
        if (nextPage !== currPage) {
          setCurrPage(nextPage);
        } else {
          fetchNotices();
        }
        setSelectedNotices([]);
      }, () => {
        toast.error("공지사항 삭제 중 오류가 발생했습니다.");
      });

    } catch (error) {
      console.log(error)
    } finally {
      closeModal();
    }
  };

  return (
    <main>
      <h2 className="adm_com_pg_tit">공지사항 목록</h2>
      <div className="adm_com_pg_content">
        <div className="adm_com_btn_wrap">
          <button className="adm_com_btn gray" onClick={openModal} disabled={notices.length === 0}>선택삭제</button>
          <button onClick={() => navigate("/Board/Notice/Write")} className="adm_com_btn black">추가</button>
        </div>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <table className='adm_com_board_list'>
            <colgroup>
              <col width="5%" />
              <col width="5%" />
              <col width="3%" />
              <col width="50%" />
              <col width="10%" />
              <col width="10%" />
              <col width="7%" />
              <col width="10%" />
            </colgroup>
            <thead>
              <tr>
                <th>
                  <div className="check">
                    <input
                      type="checkbox"
                      id="chk01"
                      className="adm_com_chk"
                      onChange={handleSelectAll}
                      checked={notices.length > 0 && selectedNotices.length === notices.length}
                      disabled={notices.length === 0}
                    />
                    <label htmlFor="chk01" />
                  </div>
                </th>
                <th>번호</th>
                <th>메인</th>
                <th>제목</th>
                <th>작성자</th>
                <th>작성일자</th>
                <th>조회수</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {notices.length === 0 && (
                <tr>
                  <td colSpan={8}>등록된 공지사항이 없습니다.</td>
                </tr>
              )}
              {notices.map((notice, index) => (
                <tr key={notice.board_seq}>
                  <td>
                    <div className="check">
                      <input
                        type="checkbox"
                        id={`chk${index + 2}`}
                        className="adm_com_chk"
                        checked={selectedNotices.includes(notice.board_seq)}
                        onChange={() => handleSelectNotice(notice.board_seq)}
                      />
                      <label htmlFor={`chk${index + 2}`} />
                    </div>
                  </td>
                  <td>{formatNumber(totalCnt - ((currPage - 1) * LIST_PAGE_SIZE + index))}</td>
                  <td>{notice.board_is_main === 'true' ? (
                    <img style={{ width: '20px' }} src={CheckedSVG} alt="end" />
                  ) : ''}</td>
                  <td className="notice_title_cell">
                    <span className="board_list_title" title={notice.board_title}>{notice.board_title}</span>
                  </td>
                  <td>{notice.author || '관리자'}</td>
                  <td>{new Date(notice.reg_dtm).toLocaleDateString()}</td>
                  <td>{formatNumber(notice.board_hit)}</td>
                  <td className="board_action_cell">
                    <button onClick={() => navigate(`/Board/Notice/Write/${notice.board_seq}`)} className="adm_com_btn blue line">수정</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {totalPages > 0 && <Paging currentPage={currPage} totalPages={totalPages} onPageChange={handlePageChange} />}
      </div>
      <Modal
        open={modalIsOpen}
        close={closeModal}
        type="confirm"
        popSize="s"
        title="삭제 확인"
        description="선택된 공지사항을 삭제하시겠습니까?"
      >
        <div className="btn_container">
          <button className="adm_com_btn blue" onClick={handleDelete}>삭제</button>
          <button className="adm_com_btn gray" onClick={closeModal}>취소</button>
        </div>
      </Modal>

      <ToastContainer />
    </main>
  );
};

export default Notice;
